/**
 * Renders a benchmark run's config (the per-run parameter overrides + folder + judge settings) so you
 * can see and compare what each run actually used. `formatParametersShort` is the one-line grid
 * version; `BenchmarkConfigDetails` is the full breakdown for the run detail header.
 */

import { effectiveInferenceParams } from "@/components/ai-connection/inferenceOptionsOverride";

const num = (value: unknown): value is number | string =>
  value !== null && value !== undefined && value !== "";

const TYPE_LABELS: Record<string, string> = {
  classify_by_agent: "Classify media",
  mail_digest: "Mail digest",
};

export const formatParametersShort = (config: any): string => {
  const gen = effectiveInferenceParams(config?.parameters);
  const p = config?.parameters ?? {};
  const parts: string[] = [];
  if (gen.model) parts.push(String(gen.model));
  if (num(gen.temperature)) parts.push(`t=${gen.temperature}`);
  if (num(gen.top_p)) parts.push(`top‑P=${gen.top_p}`);
  if (num(p.maxTokensPerSession)) parts.push(`max=${p.maxTokensPerSession}`);
  return parts.length ? parts.join(" · ") : "agent defaults";
};

export const BenchmarkConfigDetails = ({ config }: { config: any }) => {
  const gen = effectiveInferenceParams(config?.parameters);
  const p = config?.parameters ?? {};
  const judge = config?.judge ?? {};
  const input = config?.input ?? {};
  const folder = input.folder ?? config?.imageFolder; // back-compat with legacy config.imageFolder

  const rows: Array<[string, string]> = [];
  if (config?.type)
    rows.push([
      "Type",
      TYPE_LABELS[String(config.type)] ?? String(config.type),
    ]);
  if (input.type === "media" && Array.isArray(input.ids)) {
    rows.push([
      "Media records",
      `${input.ids.length} id${input.ids.length === 1 ? "" : "s"}`,
    ]);
  } else if (input.type === "eml_folder" && folder) {
    rows.push(["Email folder", String(folder)]);
  } else if (folder) {
    rows.push(["Image folder", String(folder)]);
  }
  if (gen.model) rows.push(["Model", String(gen.model)]);
  if (num(gen.temperature)) rows.push(["Temperature", String(gen.temperature)]);
  if (num(p.maxTokensPerSession))
    rows.push(["Max tokens", String(p.maxTokensPerSession)]);
  if (num(gen.top_p)) rows.push(["Top‑P", String(gen.top_p)]);
  rows.push([
    "Judge",
    judge?.enabled
      ? `${judge.mode === "image" ? "image" : "ground truth"}`
      : "off",
  ]);

  return (
    <div className="flex flex-wrap gap-2 text-xs">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="bg-white border border-gray-200 shadow-sm rounded-md px-3 py-1.5 flex items-center gap-2"
        >
          <span className="text-gray-500 font-medium uppercase tracking-wider text-[10px]">
            {label}
          </span>
          <span className="text-gray-900 font-semibold">{value}</span>
        </div>
      ))}
    </div>
  );
};
